/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { Multinomial, util } from '@tensorflow/tfjs-core';
import * as seedrandom from 'seedrandom';
import { assertNotComplex } from '../cpu_util';
import { softmax } from './Softmax';
export function multinomial(args) {
  var inputs = args.inputs,
    backend = args.backend,
    attrs = args.attrs;
  var logits = inputs.logits;
  var numSamples = attrs.numSamples,
    seed = attrs.seed,
    normalized = attrs.normalized;
  assertNotComplex(logits, 'multinomial');
  var probabilities = normalized ? logits : softmax({
    inputs: {
      logits: logits
    },
    backend: backend,
    attrs: {
      dim: -1
    }
  });
  var batchSize = probabilities.shape[0];
  var numEvents = probabilities.shape[1];
  var probVals = backend.data.get(probabilities.dataId).values;
  var resShape = [batchSize, numSamples];
  var resVals = util.makeZerosTypedArray(util.sizeFromShape(resShape), 'int32');
  for (var b = 0; b < batchSize; ++b) {
    var offset = b * numEvents;
    // The cdf won't include the last event. It will be implicit if no other
    // event happened.
    var cdf = new Float32Array(numEvents - 1);
    cdf[0] = probVals[offset];
    for (var event = 1; event < cdf.length; ++event) {
      cdf[event] = cdf[event - 1] + probVals[offset + event];
    }
    var random = seedrandom.alea(seed.toString());
    var outOffset = b * numSamples;
    for (var sampleId = 0; sampleId < numSamples; ++sampleId) {
      var r = random();
      // Assume last event happened by default.
      resVals[outOffset + sampleId] = cdf.length;
      for (var _event = 0; _event < cdf.length; _event++) {
        if (r < cdf[_event]) {
          resVals[outOffset + sampleId] = _event;
          break;
        }
      }
    }
  }
  if (!normalized) {
    backend.disposeIntermediateTensorInfo(probabilities);
  }
  return backend.makeTensorInfo(resShape, 'int32', resVals);
}
export var multinomialConfig = {
  kernelName: Multinomial,
  backendName: 'cpu',
  kernelFunc: multinomial
};